import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 sm:px-6 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/register" 
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Registration
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-neutral-400">Last updated: March 01, 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-neutral-300">
            <strong>R3V Ventures INC.</strong> ("we", "our", or "us") operates RydQuest, a car community platform for events, sidequests, and related automotive content.
          </p>
          <p className="text-neutral-300">
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website.
          </p>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">1. Information We Collect</h2>
            <p className="text-neutral-300">We may collect the following types of information:</p>
            
            <h3 className="text-lg font-medium text-white mt-4">Personal Information</h3>
            <ul className="list-disc list-inside text-neutral-300 space-y-1 ml-4">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Account login credentials</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4">Usage Information</h3>
            <ul className="list-disc list-inside text-neutral-300 space-y-1 ml-4">
              <li>Device and browser information</li>
              <li>IP address</li>
              <li>Pages visited and actions taken</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4">Booking & Communication Data</h3>
            <ul className="list-disc list-inside text-neutral-300 space-y-1 ml-4">
              <li>Booking details</li>
              <li>Messages sent via SMS or email</li>
              <li>Business interaction data</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">2. How We Use Your Information</h2>
            <p className="text-neutral-300">We use your information to:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-1 ml-4">
              <li>Create and manage your account</li>
              <li>Process bookings and send confirmations</li>
              <li>Send notifications via email or SMS</li>
              <li>Connect you with businesses on the platform</li>
              <li>Improve our services and user experience</li>
              <li>Prevent fraud and ensure platform security</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">3. Communications</h2>
            <p className="text-neutral-300">By using our platform, you agree to receive:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-1 ml-4">
              <li>Booking confirmations</li>
              <li>Status updates (approved/declined bookings)</li>
              <li>Service-related notifications</li>
            </ul>
            <p className="text-neutral-300 mt-2">These may be sent via:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-1 ml-4">
              <li>Email</li>
              <li>SMS (text messages)</li>
            </ul>
            <p className="text-neutral-300 mt-2">Standard messaging rates may apply.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">4. Sharing of Information</h2>
            <p className="text-neutral-300">We may share your information with:</p>
            
            <h3 className="text-lg font-medium text-white mt-4">Businesses on the Platform</h3>
            <p className="text-neutral-300 ml-4">To fulfill bookings and provide services</p>

            <h3 className="text-lg font-medium text-white mt-4">Service Providers</h3>
            <ul className="list-disc list-inside text-neutral-300 space-y-1 ml-4">
              <li>Email delivery services</li>
              <li>SMS providers (e.g., Twilio or similar)</li>
              <li>Hosting and infrastructure providers</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4">Legal Requirements</h3>
            <p className="text-neutral-300 ml-4">If required by law or to protect our rights</p>

            <p className="text-neutral-300 mt-4 font-medium">We do not sell your personal data.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">5. Data Storage & Security</h2>
            <p className="text-neutral-300">We store your data using secure infrastructure and take reasonable measures to protect it.</p>
            <p className="text-neutral-300">However, no method of transmission or storage is 100% secure.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">6. Your Rights (Canada)</h2>
            <p className="text-neutral-300">You have the right to:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-1 ml-4">
              <li>Access your personal data</li>
              <li>Request corrections</li>
              <li>Request deletion of your data</li>
            </ul>
            <p className="text-neutral-300 mt-2">
              To make a request, contact us at: <a href="mailto:info@r3vit.com" className="text-red-400 hover:text-red-300">info@r3vit.com</a>
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">7. Cookies & Tracking</h2>
            <p className="text-neutral-300">We may use cookies or similar technologies to:</p>
            <ul className="list-disc list-inside text-neutral-300 space-y-1 ml-4">
              <li>Improve user experience</li>
              <li>Analyze usage</li>
              <li>Support platform functionality</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">8. Third-Party Services</h2>
            <p className="text-neutral-300">Our platform may use third-party services that collect data in accordance with their own privacy policies.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">9. Changes to This Policy</h2>
            <p className="text-neutral-300">We may update this Privacy Policy from time to time. Updates will be posted on this page.</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-white">10. Contact Us</h2>
            <p className="text-neutral-300">If you have questions about this Privacy Policy, contact us at:</p>
            <p className="text-neutral-300">
              <strong>R3V Ventures INC.</strong><br />
              Email: <a href="mailto:info@r3vit.com" className="text-red-400 hover:text-red-300">info@r3vit.com</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
